import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Assignment as ReportsIcon, Add as AddIcon, PhotoCamera as CameraIcon, UploadFile as FileIcon,
  PictureAsPdf as PdfIcon, Image as ImageIcon, OpenInNew as OpenIcon, DeleteOutline as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const TYPE_LABEL = {
  blood: 'Blood test', urine: 'Urine test', imaging: 'Imaging / scan', ecg: 'ECG',
  pathology: 'Pathology', prescription: 'Prescription', letter: 'Letter', other: 'Other',
};
export const BLOOD_TEST_LABEL = {
  cbc: 'Complete blood count (CBC)', lipid: 'Lipid profile (cholesterol)', hba1c: 'HbA1c (diabetes)', glucose: 'Blood sugar',
  lft: 'Liver function (LFT)', kft: 'Kidney function (KFT)', thyroid: 'Thyroid (TSH, T3, T4)',
  vitamin_d: 'Vitamin D', vitamin_b12: 'Vitamin B12', iron: 'Iron studies', electrolytes: 'Electrolytes',
  crp_esr: 'Inflammation (CRP / ESR)', other: 'Other blood test',
};
/** "Blood test · Lipid profile", or just the type. */
export const reportKindLabel = (r) => [TYPE_LABEL[r.report_type] || r.report_type || 'Report',
  r.report_type === 'blood' && r.blood_test ? BLOOD_TEST_LABEL[r.blood_test] || null : null].filter(Boolean).join(' · ');
const MAX_BYTES = 15 * 1024 * 1024;

const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
  .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// A phone photo is 4–10 MB; re-encode it at a size a lab report is still readable at.
async function shrinkPhoto(file) {
  if (!file.type.startsWith('image/') || file.size < 1.5 * 1024 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/** Open a report in a new tab (fetched with the session, handed over as a blob). */
export async function openFile(url) {
  const tab = window.open('', '_blank');
  try {
    const res = await axios.get(url, { responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    if (tab) tab.location.href = href; else window.location.href = href;
  } catch {
    if (tab) tab.close();
    toast.error('Could not open that report');
  }
}

/**
 * The patient's reports — the ones they added and the ones their clinics added. They
 * can add a PDF or a photo taken with the phone, to any clinic they're a patient of,
 * and link it to a visit there; the doctor from that visit is told.
 */
export default function Reports() {
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ clinics: [], visits: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const camera = useRef(null);
  const picker = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([
        axios.get(`${API_URL}/api/v1/reports/mine`),
        axios.get(`${API_URL}/api/v1/reports/mine/options`),
      ]);
      setRows(r.data.reports || []);
      setOpts(o.data || { clinics: [], visits: [] });
    } catch {
      toast.error('Could not load your reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setForm({ patient_id: opts.clinics[0]?.patient_id || '', title: '', report_type: 'blood',
      blood_test: '', report_date: '', notes: '', session_id: '' });
    setFile(null);
    setAdding(true);
  };

  const choose = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const ready = await shrinkPhoto(f);
    if (ready.size > MAX_BYTES) { toast.error('That file is over 15 MB. Please use a smaller one.'); return; }
    setFile(ready);
    if (!form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, '').slice(0, 80) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      Object.entries(form).forEach(([k, v]) => { if (v) body.append(k, v); });
      await axios.post(`${API_URL}/api/v1/reports/mine`, body);
      toast.success(form.session_id ? 'Report added — your doctor has been told' : 'Report added');
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the report');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete “${r.title}”?`)) return;
    try {
      await axios.delete(`${API_URL}/api/v1/reports/mine/${r.id}`);
      toast.success('Report deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete it');
    }
  };

  const visitsHere = opts.visits.filter((v) => v.patient_id === form?.patient_id);
  const set = (k) => (e) => setForm({
    ...form, [k]: e.target.value,
    ...(k === 'patient_id' ? { session_id: '' } : {}),
    ...(k === 'report_type' ? { blood_test: '' } : {}),   // only a blood report has a blood test
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><ReportsIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}
            sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407' }}>My reports</Typography>
          <Typography variant="body2" color="text.secondary">Blood tests, scans and letters — yours and your clinic’s.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }} onClick={startAdd}
          disabled={!opts.clinics.length}>
          Add a report
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !opts.clinics.length ? (
        <Alert severity="info">Once a clinic registers you as a patient, you can add reports for them here.</Alert>
      ) : !rows.length ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 5 }}>
          <ReportsIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No reports yet. Add a PDF, or take a photo of a paper report.</Typography>
        </CardContent></Card>
      ) : (
        <Grid container spacing={2}>
          {rows.map((r) => (
            <Grid item xs={12} md={6} key={r.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    {r.content_type === 'application/pdf' ? <PdfIcon color="error" /> : <ImageIcon color="primary" />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap>{r.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reportKindLabel(r)}{r.report_date ? ` · ${fmt(r.report_date)}` : ''}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" label={r.clinic_name} />
                        {r.session_id && <Chip size="small" icon={<LinkIcon />} color="primary" variant="outlined" label={`Visit ${fmt(r.visit_date)}`} />}
                        <Chip size="small" variant="outlined"
                          label={r.uploaded_by_role === 'patient' ? 'Added by you' : `Added by ${r.clinic_name}`} />
                      </Stack>
                    </Box>
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => openFile(`${API_URL}/api/v1/reports/mine/${r.id}/file`)}><OpenIcon /></IconButton>
                    </Tooltip>
                    {r.uploaded_by_role === 'patient' && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={adding} onClose={() => !busy && setAdding(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a report</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField select label="Clinic" value={form.patient_id} onChange={set('patient_id')}>
                {opts.clinics.map((c) => <MenuItem key={c.patient_id} value={c.patient_id}>{c.clinic_name}</MenuItem>)}
              </TextField>
              <Box sx={{ p: 2, border: '1px dashed', borderColor: file ? 'primary.main' : 'divider', borderRadius: 2, textAlign: 'center' }}>
                {file ? (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    {file.type === 'application/pdf' ? <PdfIcon color="error" /> : <ImageIcon color="primary" />}
                    <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{file.name}</Typography>
                    <Button size="small" onClick={() => setFile(null)}>Change</Button>
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
                    <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => camera.current?.click()}>Take photo</Button>
                    <Button variant="outlined" startIcon={<FileIcon />} onClick={() => picker.current?.click()}>Choose PDF or image</Button>
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  PDF or photo, up to 15 MB. Photos are resized for you.
                </Typography>
                <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={choose} />
                <input ref={picker} type="file" accept="application/pdf,image/*" hidden onChange={choose} />
              </Box>
              <TextField label="Title" value={form.title} onChange={set('title')} required placeholder="e.g. Cholesterol test" />
              <Stack direction="row" spacing={2}>
                <TextField select label="Type" value={form.report_type} onChange={set('report_type')} sx={{ flex: 1 }}>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <TextField type="date" label="Report date" InputLabelProps={{ shrink: true }}
                  value={form.report_date} onChange={set('report_date')} sx={{ flex: 1 }} />
              </Stack>
              {form.report_type === 'blood' && (
                <TextField select label="Which blood test?" value={form.blood_test} onChange={set('blood_test')}>
                  <MenuItem value=""><em>Not sure</em></MenuItem>
                  {Object.entries(BLOOD_TEST_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
              )}
              <TextField select label="Link to a visit (optional)" value={form.session_id} onChange={set('session_id')}
                helperText={form.session_id ? 'The doctor from this visit will be notified.' : (visitsHere.length ? ' ' : 'No completed visits at this clinic yet.')}>
                <MenuItem value=""><em>Not linked to a visit</em></MenuItem>
                {visitsHere.map((v) => (
                  <MenuItem key={v.session_id} value={v.session_id}>{fmt(v.date)} — {v.doctor_name || 'Visit'}</MenuItem>
                ))}
              </TextField>
              <TextField label="Notes (optional)" value={form.notes} onChange={set('notes')} multiline minRows={2} />
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdding(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !file || !form?.title?.trim() || !form?.patient_id}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {busy ? 'Uploading…' : 'Add report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
